import createClient, { type Middleware } from "openapi-fetch";

import { useAuthStore } from "@/store/auth";
import type { paths } from "@/types/api";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

export const apiClient = createClient<paths>({ baseUrl: BASE_URL });

/**
 * Raw JSON request that mirrors the apiClient auth + organization middleware.
 * Use this for endpoints not yet present in the generated `paths` type
 * (i.e. before the next openapi regeneration). Once a path lands in api.ts,
 * prefer the typed `apiClient` instead.
 */
export async function rawRequest<T>(
  method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  const { token, currentOrganizationId } = useAuthStore.getState();
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;
  if (currentOrganizationId) headers["X-Organization-Id"] = currentOrganizationId;

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const err = (await res.json()) as { detail?: string };
      if (typeof err.detail === "string") message = err.detail;
    } catch {
      // ignore parse failure, keep generic message
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

/** Attach the access token + organization context header to every request. */
const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const { token, currentOrganizationId } = useAuthStore.getState();
    if (token) request.headers.set("Authorization", `Bearer ${token}`);
    if (currentOrganizationId)
      request.headers.set("X-Organization-Id", currentOrganizationId);
    return request;
  },
};

let refreshing: Promise<boolean> | null = null;

async function runRefresh(): Promise<boolean> {
  const { refreshToken, setSession, clearSession } = useAuthStore.getState();
  if (!refreshToken) {
    clearSession();
    return false;
  }
  try {
    const res = await fetch(`${BASE_URL}/api/v1/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) {
      clearSession();
      return false;
    }
    const data = await res.json();
    setSession({ token: data.access_token, refreshToken: data.refresh_token });
    return true;
  } catch {
    clearSession();
    return false;
  }
}

/** On 401, attempt a single coalesced token refresh, then retry once. */
const refreshMiddleware: Middleware = {
  async onResponse({ request, response }) {
    if (response.status !== 401 || request.url.includes("/auth/")) return response;

    refreshing ??= runRefresh().finally(() => {
      refreshing = null;
    });
    const ok = await refreshing;
    if (!ok) {
      if (typeof window !== "undefined") window.location.assign("/login");
      return response;
    }

    const { token } = useAuthStore.getState();
    const retried = request.clone();
    if (token) retried.headers.set("Authorization", `Bearer ${token}`);
    return fetch(retried);
  },
};

apiClient.use(authMiddleware);
apiClient.use(refreshMiddleware);
